import { ICreditBooksFilterValues } from "./CreditBooksFilterDrawer";

export function emptyFilters(): ICreditBooksFilterValues {
  return {
    balance_min: "",
    balance_max: "",
    customer_type_ids: [],
    tag_ids: [],
  };
}

export const emptyDrawerFilters = emptyFilters;

export function countActiveFilters(filters: ICreditBooksFilterValues): number {
  let count = 0;
  if (filters.balance_min !== "" || filters.balance_max !== "") {
    count++;
  }
  if (filters.customer_type_ids.length > 0) {
    count++;
  }
  if (filters.tag_ids.length > 0) {
    count++;
  }
  return count;
}

export function hasActiveFilters(filters: ICreditBooksFilterValues): boolean {
  return countActiveFilters(filters) > 0;
}

export function formatBalanceFilterValue(balanceMin: string, balanceMax: string): string {
  const min = balanceMin !== "" ? `$${balanceMin}` : "";
  const max = balanceMax !== "" ? `$${balanceMax}` : "";

  if (min && max) {
    return `${min}-${max}`;
  }
  if (min) {
    return `${min}+`;
  }
  return `-${max}`;
}

export function formatNamesWithMore(names: string[], maxVisible: number, expanded: boolean): string {
  if (names.length === 0) {
    return "";
  }

  if (expanded || names.length <= maxVisible) {
    return names.join(", ");
  }

  const visible = names.slice(0, maxVisible);
  const remaining = names.length - maxVisible;

  return `${visible.join(", ")}, + ${remaining} more`;
}

// Matches $lightBlue in public/scss/_variables.scss and Figma Blue-50 / Blue-200 tokens.
const TAG_BLUE_TEXT = "#175CD3";
const TAG_BLUE_BORDER = "#B2DDFF";
const TAG_BLUE_BACKGROUND = "#EFF8FF";

export function getTagBadgeColours(colour?: string): {
  textColour: string;
  borderColour: string;
  backgroundColour: string;
} {
  const fallback = {
    textColour: "#344054",
    borderColour: "#EAECF0",
    backgroundColour: "transparent",
  };

  if (!colour) {
    return fallback;
  }

  const normalizedColour = normalizeHexColour(colour);

  if (normalizedColour === TAG_BLUE_TEXT) {
    return {
      textColour: TAG_BLUE_TEXT,
      borderColour: TAG_BLUE_BORDER,
      backgroundColour: TAG_BLUE_BACKGROUND,
    };
  }

  const rgb = parseHexColour(normalizedColour);
  if (!rgb) {
    return {
      textColour: colour,
      borderColour: colour,
      backgroundColour: "transparent",
    };
  }

  // Tuned to match Figma Blue-50 (#EFF8FF) / Blue-200 (#B2DDFF) for typical tag blues.
  return {
    textColour: normalizedColour,
    borderColour: mixRgbWithWhite(rgb, 0.3),
    backgroundColour: mixRgbWithWhite(rgb, 0.08),
  };
}

function normalizeHexColour(colour: string): string {
  let hex = colour.trim().substring(0, 7);

  if (hex.length === 4) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }

  return hex.toUpperCase();
}

function parseHexColour(colour: string): { red: number; green: number; blue: number } | null {
  const hex = normalizeHexColour(colour);
  const match = /^#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})$/.exec(hex);

  if (!match) {
    return null;
  }

  return {
    red: parseInt(match[1], 16),
    green: parseInt(match[2], 16),
    blue: parseInt(match[3], 16),
  };
}

function mixRgbWithWhite(
  rgb: { red: number; green: number; blue: number },
  colourWeight: number,
): string {
  const whiteWeight = 1 - colourWeight;

  const red = Math.round(rgb.red * colourWeight + 255 * whiteWeight);
  const green = Math.round(rgb.green * colourWeight + 255 * whiteWeight);
  const blue = Math.round(rgb.blue * colourWeight + 255 * whiteWeight);

  return `#${[red, green, blue].map(channel => channel.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}
